import { useEffect, useMemo } from 'react';
import { SSHService } from '../../../core/services/ssh.service';
import { DestinationFactory } from '../factories/destinationFactory';
import { useQuery } from '../../../core/hooks/useQuery';
import useService from '../../../injector/hooks/useService';
import { DESTINATION_SCHEMA_POLL_INTERVAL } from '../constants/constants';
import { useGetDestinationApi } from './useGetDestinationApi';

export function useDestination({ destinationId }) {
  const getDestinationApi = useGetDestinationApi();

  /**
   * DestinationAPI depends on SSHService.
   * Declaring this to initialise sshService if not initialised by any ancestors.
   */
  const sshService = useService(SSHService);

  /**
   * this is required
   */
  const {
    data: rawDestination,
    fetch: getDestination,
    refetch: refetchDestination,
    uiState: destinationUIState,
    error: destinationFetchError,
    writeData: writeDestinationData
  } = useQuery({
    dataSource$: () => getDestinationApi(
      destinationId,
      { schemas: true, relations: true },
      false
    ).then(res => res.data),
    pollInterval: DESTINATION_SCHEMA_POLL_INTERVAL
  });

  const onSchemaDelete = (schemaName: string) => {
    const schemas = [ ...rawDestination.schemas ];
    const deleteIndex = schemas.findIndex((schema) => schema.name === schemaName);
    schemas.splice(deleteIndex, 1);

    writeDestinationData((d) => ({
      ...d,
      schemas
    }));
  };

  useEffect(() => {
    if (destinationId) {
      getDestination();
    }
  }, [ destinationId ]);

  const destination = useMemo(() => {
    return DestinationFactory(rawDestination);
  }, [ rawDestination ]);

  return {
    rawDestination,
    destination,
    destinationUIState: {
      state: destinationUIState,
      error: destinationFetchError
    },
    getDestination,
    onSchemaDelete,
    refetchDestination
  };
}
